import React, { useEffect, useState, useRef } from 'react'
import {
    Box,
    Modal,
    Typography,
    Link,
    Grid,
    Tooltip,
    Avatar,
    styled,
} from '@mui/material'
import { TextIconButtonV2 as TextIconButton } from 'components'
import { ReactComponent as CloseIconSvg } from 'icons/close-icon.svg'
import CustomButton from 'components/CustomButton'
import DefaultIconSvg from 'icons/default.svg'
import CropIconSvg from 'icons/crop.svg'
import CropIconSvgActive from 'icons/crop-active.svg'
import ImageEditor from './ImageEditor'
import appConfig from 'config'
import { useTranslation } from 'react-i18next'

const StyledModal = styled(Box)(({ theme }) => ({
    overflowY: 'hidden',
    position: 'absolute',
    maxHeight: '96vh',
    [theme.breakpoints.up('sm')]: {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '60rem',
    },

    [theme.breakpoints.up('md')]: {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80rem',
    },

    [theme.breakpoints.down('sm')]: {
        maxWidth: '40rem',
        margin: '1rem auto',
        left: 0,
        right: 0,
    },

    backgroundColor: '#f7f8fa',
    borderRadius: '0.8rem',
    boxShadow: '0 0 1.3rem 0 rgba(0, 0, 0, 0.15)',
    padding: 0,
    '&:focus': {
        outline: 'none',
    },
}))

const StyledModalHeader = styled(Box)({
    display: 'flex',
    backgroundColor: '#fff',
    justifyContent: 'space-between',
    padding: '1.5rem 2rem ',
    height: '10%',
    minHeight: '55px',
    borderBottom: '1px solid #e2dddd',
})

const StyledModalFooter = styled(Box)({
    borderTop: '1px solid #e2dddd',
    display: 'flex',
    backgroundColor: '#fff',
    width: '100%',
    height: '16%',
    padding: '1.5rem 2rem ',
    overflowY: 'auto',
    minHeight: '67px',
    '@media (max-width:350px)': {
        padding: '1.5rem 1rem',
    },
})

const StyledLeftActionGridItem = styled(Grid)({
    textAlign: 'right',
})

const StyledOpenEditorLeftActionGridItem = styled(Grid)({
    textAlign: 'start',
})

const StyledOpenEditorRightActionGridItem = styled(Grid)({
    textAlign: 'end',
    '& button': {
        '&:not(:last-child)': {
            marginRight: '20px',
        },
    },
    '@media (max-width:600px)': {
        textAlign: 'center',
        '& button': {
            top: '-10px',
        },
    },
    '@media (max-width:380px)': {
        '& button': {
            '&:not(:last-child)': {
                marginRight: '5px !important',
            },
        },
    },
})

const StyledModalContent = styled(Box)({
    textAlign: 'center',
    margin: '0 !important',
    display: 'block !important',
    maxHeight: '65vh',
    backgroundColor: '#fbfbfd',
    overflowY: 'auto',
})

const StyledEditorModalContent = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    margin: '0 !important',
    display: 'flex !important',
    height: '60%',
    backgroundColor: 'black',
    padding: '0 !important',
    [theme.breakpoints.down('sm')]: {
        height: '300px',
    },
}))

const StyledEditorBox = styled(Box)({
    height: '14%',
    position: 'relative',
    top: '6px',
    padding: '5px 30px',
    '& button': {
        '&:not(:last-child)': {
            marginRight: '30px',
        },
    },
    '@media (max-width:600px)': {
        '& > div': {
            marginTop: '-5px',
        },
    },
})

const StyledDeleteButton = styled('span')({
    cursor: 'pointer',
    position: 'relative',
    display: 'block',
    marginBottom: '4px',
    top: '10px',
    '@media (max-width:600px)': {
        textAlign: 'center',
        top: 0,
    },
})

const StyledDescTypo = styled(Typography)({
    fontSize: '14px',
})

const StyledContentBox = styled(Box)({
    width: '100%',
    height: '100%',
    margin: '0 !important',
    display: 'flex !important',
})

const StyledEditorContentBox = styled(Box)({
    width: '100%',
    height: '100%',
    margin: '0 !important',
    padding: '0 !important',
    position: 'relative',
})

const StyledBigAvatar = styled(Avatar)({
    width: '159.8px',
    height: '160.7px',
})

const MessageTypography = styled(Typography)(({ variant }) => ({
    fontSize: '1.4rem',
    fontWeight: 'normal',
    padding: '0 1rem 2rem',
    ...(variant === 'body2' && {
        color: '#ff0000',
    }),
}))

const ProfilePictureModal = ({
    open,
    onClose,
    header,
    fields,
    loading,
    data,
    updateData,
    actions,
    response,
}) => {
    const [state, setState] = useState({
        data: {},
        scale: 0,
        straighten: 0,
    })
    const childRef = useRef()
    const fileInput = useRef()

    useEffect(() => {
        //get data
        setState({
            data: data,
        })
    }, [data])

    const handleFileUpload = (e) => {
        const file = e.target.files[0]
        if (!file) return
        // allowed MIME types
        const mime_types = ['image/jpeg', 'image/png']

        // validate MIME
        if (mime_types.indexOf(file.type) === -1) {
            alert('Error : Incorrect file type')
            return
        }

        // validate file size
        if (file.size > 2 * 1024 * 1024) {
            alert('Error : Exceeded size 2MB')
            return
        }

        const _PREVIEW_URL = URL.createObjectURL(file)

        setState({
            ...state,
            avatarPreviewUrl: _PREVIEW_URL,
            openEditor: true,
            selectedFile: file,
            scale: 0,
            straighten: 0,
        })
    }

    const setEditedImageLocal = (imageURI) => {
        setState({
            ...state,
            openEditor: false,
            avatar_uri: imageURI,
        })
    }

    const uploadEditedImageToServer = () => {
        const { avatar_uri } = state
        const uploadParams = {
            type: 'image/png',
            dataURI: avatar_uri,
        }

        if (!avatar_uri) {
            console.log('Picture not updated')
        } else {
            console.log('==>Save Edited Image to Server : ')
            console.log('==>uploadParams :', uploadParams, '\n')
        }

        const avatar = state.avatar_uri ? state.avatar_uri : state.data.avatar

        updateData({
            ...state.data,
            avatar,
            avatarPreviewUrl: state.avatarPreviewUrl,
        })
    }

    const deletePicture = (e) => {
        setState({
            ...state,
            data: {
                ...data,
            },
            avatar_uri: null,
            openEditor: false,
        })
    }
    const { t } = useTranslation()
    return (
        <div>
            <Modal
                aria-labelledby="simple-modal-title"
                open={open}
                onClose={onClose}
            >
                <StyledModal>
                    <StyledModalHeader>
                        <Typography id="simple-modal-title" variant="h6">
                            {state.openEditor ? t('EditPhoto') : header}
                        </Typography>
                        <Box>
                            <Link
                                component="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-ai-explain="Close photo dialog"
                            >
                                <CloseIconSvg></CloseIconSvg>
                            </Link>
                        </Box>
                    </StyledModalHeader>

                    <Box
                        component={
                            state.openEditor
                                ? StyledEditorModalContent
                                : StyledModalContent
                        }
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        m={1}
                        p={1}
                    >
                        {fields.map((item, index) => {
                            switch (item.type) {
                                case 'AvatarImage':
                                    return (
                                        <Box
                                            key={'edit-item-' + index}
                                            component={
                                                state.openEditor
                                                    ? StyledEditorContentBox
                                                    : StyledContentBox
                                            }
                                            display="flex"
                                            flexDirection="column"
                                            justifyContent="center"
                                            alignItems="center"
                                            m={1}
                                            p={1}
                                        >
                                            {!state.openEditor && (
                                                <Typography variant="h5">
                                                    {item.label}
                                                </Typography>
                                            )}

                                            {state.openEditor ? (
                                                <>
                                                    {
                                                        <ImageEditor
                                                            srcFile={
                                                                state[
                                                                    'selectedFile'
                                                                ]
                                                            }
                                                            scale={
                                                                state[
                                                                    'scale'
                                                                ] || 0
                                                            }
                                                            straighten={
                                                                state[
                                                                    'straighten'
                                                                ] || 0
                                                            }
                                                            showState={true}
                                                            ref={childRef}
                                                        />
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    <br />
                                                    <br />
                                                    <StyledBigAvatar
                                                        alt=""
                                                        src={
                                                            state.avatar_uri ||
                                                            `${appConfig.BASE_EID_URL}${data.pathToImage}` ||
                                                            state.data.avatar
                                                        }
                                                    />
                                                </>
                                            )}
                                            {!state.openEditor && (
                                                <>
                                                    <br />
                                                    <br />
                                                    <StyledDescTypo>
                                                        {item.description || ''}
                                                    </StyledDescTypo>
                                                    <MessageTypography
                                                        variant={
                                                            response &&
                                                            !response.success &&
                                                            'body2'
                                                        }
                                                    >
                                                        {response &&
                                                            response.message}
                                                    </MessageTypography>
                                                </>
                                            )}
                                        </Box>
                                    )
                                default:
                                    return <></>
                            }
                        })}
                    </Box>

                    {state.openEditor && (
                        <StyledEditorBox>
                            <Grid container spacing={2}>
                                <StyledOpenEditorLeftActionGridItem
                                    item
                                    xs={11}
                                    style={{}}
                                >
                                    <TextIconButton
                                        text="Crop"
                                        iconActive={CropIconSvgActive}
                                        icon={CropIconSvg}
                                        active={true}
                                        data-ai-explain="Crop profile photo"
                                    />
                                </StyledOpenEditorLeftActionGridItem>
                                <StyledOpenEditorRightActionGridItem
                                    item
                                    xs={1}
                                >
                                    <Tooltip
                                        enterTouchDelay={0}
                                        placement="top"
                                        title={t('UndoAllChanges')}
                                    >
                                        <img
                                            src={`${appConfig.APP_SUBPATH}${DefaultIconSvg}`}
                                            alt=""
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Undo all changes"
                                            style={{
                                                position: 'relative',
                                                top: '7px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                childRef.current.resetImage()
                                            }}
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault()
                                                    childRef.current.resetImage()
                                                }
                                            }}
                                            data-ai-explain="Undo all photo crop changes"
                                        />
                                    </Tooltip>
                                </StyledOpenEditorRightActionGridItem>
                            </Grid>
                        </StyledEditorBox>
                    )}

                    <StyledModalFooter
                        justifyContent="center"
                        alignItems="center"
                        display="flex"
                        flexDirection="row"
                    >
                        {state.openEditor ? (
                            <Grid container spacing={2}>
                                <StyledOpenEditorLeftActionGridItem
                                    item
                                    xs={12}
                                    sm={3}
                                    style={{}}
                                >
                                    <StyledDeleteButton
                                        onClick={deletePicture}
                                        data-ai-explain="Delete current profile photo"
                                    >
                                        {t('DeletePhoto')}
                                    </StyledDeleteButton>
                                </StyledOpenEditorLeftActionGridItem>
                                <StyledOpenEditorRightActionGridItem
                                    item
                                    xs={12}
                                    sm={9}
                                >
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        hidden
                                        aria-label="Change photo"
                                        onChange={handleFileUpload}
                                    />
                                    <CustomButton
                                        label={
                                            actions &&
                                            actions[0] &&
                                            actions[0].label
                                                ? actions[0].label
                                                : t('SaveChanges')
                                        }
                                        onClick={() => {
                                            fileInput.current.click()
                                        }}
                                        width={150}
                                        m={5}
                                        type={'file'}
                                        active={
                                            actions &&
                                            actions[0] &&
                                            actions[0].active
                                        }
                                        data-ai-explain="Save cropped photo"
                                    ></CustomButton>
                                    <CustomButton
                                        label={
                                            actions &&
                                            actions[1] &&
                                            actions[1].label
                                                ? actions[1].label
                                                : t('DiscardChanges')
                                        }
                                        width={150}
                                        p={5}
                                        onClick={() => {
                                            const editedImage =
                                                childRef.current.getEdittedImage()
                                            return setEditedImageLocal(
                                                editedImage,
                                            )
                                        }}
                                        active={
                                            actions &&
                                            actions[1] &&
                                            actions[1].active
                                        }
                                        data-ai-explain="Discard photo crop changes"
                                    ></CustomButton>
                                </StyledOpenEditorRightActionGridItem>
                            </Grid>
                        ) : (
                            <Grid container spacing={2}>
                                <StyledLeftActionGridItem item xs={6}>
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        hidden
                                        aria-label="Change photo"
                                        onChange={handleFileUpload}
                                    />
                                    <CustomButton
                                        label={
                                            actions &&
                                            actions[0] &&
                                            actions[0].label
                                                ? actions[0].label
                                                : t('ChangePhoto')
                                        }
                                        onClick={() => {
                                            fileInput.current.click()
                                        }}
                                        width={150}
                                        m={5}
                                        type={'file'}
                                        disabled={loading}
                                        active={
                                            actions &&
                                            actions[0] &&
                                            actions[0].active
                                        }
                                        data-ai-explain="Upload a new profile photo"
                                    ></CustomButton>
                                </StyledLeftActionGridItem>
                                <Grid item xs={6}>
                                    <CustomButton
                                        label={
                                            actions &&
                                            actions[1] &&
                                            actions[1].label
                                                ? actions[1].label
                                                : t('SaveChanges')
                                        }
                                        width={150}
                                        p={5}
                                        disabled={loading}
                                        loading={loading}
                                        onClick={() => {
                                            uploadEditedImageToServer()
                                        }}
                                        active={
                                            actions &&
                                            actions[1] &&
                                            actions[1].active
                                        }
                                        data-ai-explain="Save profile photo changes"
                                    ></CustomButton>
                                </Grid>
                            </Grid>
                        )}
                    </StyledModalFooter>
                </StyledModal>
            </Modal>
        </div>
    )
}

export default ProfilePictureModal
